import { useEffect, useRef, useState, type CSSProperties } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/components/language-provider";
import { CINEMATIC, type FrameSet } from "@/components/landing/cinematic-manifest";

gsap.registerPlugin(ScrollTrigger, useGSAP);

// Everything below is laid out in vh of pinned travel (0 → CINEMATIC.trackVh), the same unit the
// frame anchors use, so film, captions and handoff stay on one timeline in both scroll directions.
const TRACK = CINEMATIC.trackVh;
const HERO_OUT = 28;
const BEATS: Array<[inStart: number, outStart: number]> = [
  [50, 140],  // B1 — AI marks the line, the pen signs (paper)
  [250, 290], // B2 — the same signature on the phone, WhatsApp notification
  [305, 350], // B3 — legal: phone with the seal
];
const FADE = 10;
const ZOOM = 420; // camera enters the laptop screen; the film ends on the exact first view of the "inside Dynamics" section
const RAIL = [BEATS[0][0], BEATS[1][0], BEATS[2][0], 360]; // side progress rail (pear.no style), one tick per chapter

type Props = { pt: boolean; ctaHref: string; ctaLabel: string; signup: boolean; onVideo: () => void; compact?: boolean };

// Static mode never downloads the frame sequence: reduced motion or an explicit data-saver.
function prefersStatic(): boolean {
  if (typeof window === "undefined") return true;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;
  return reduce || saveData;
}

const portraitQuery = "(orientation: portrait)";

// Load order: the keyframes first (incl. the last frame, which must match the Dynamics section at the handoff),
// then coarse-to-fine so the whole path becomes scrubbable at low temporal resolution quickly.
function loadOrder(set: FrameSet): number[] {
  const n = set.count;
  const seen = new Uint8Array(n);
  const out: number[] = [];
  const add = (i: number) => { if (i >= 0 && i < n && !seen[i]) { seen[i] = 1; out.push(i); } };
  [0, n - 1, ...set.anchors, set.containFrom].forEach(add);
  for (const step of [16, 8, 4, 2, 1]) for (let i = 0; i < n; i += step) add(i);
  return out;
}

function frameAt(vh: number, set: FrameSet): number {
  const a = CINEMATIC.anchorsVh;
  if (vh <= a[0]) return set.anchors[0];
  for (let k = 1; k < a.length; k++) {
    if (vh <= a[k]) {
      const t = (vh - a[k - 1]) / (a[k] - a[k - 1]);
      return Math.round(set.anchors[k - 1] + t * (set.anchors[k] - set.anchors[k - 1]));
    }
  }
  return set.anchors[set.anchors.length - 1];
}

const PAPER = "#FAF7F0";

// object-fit cover (or contain, for a letterboxed set) with a focal point. During the zoom into the laptop the cover fit
// eases into a centered contain fit on the Dynamics background, which is exactly how the next section lays out its window.
const HEADER = 64; // the site menu stays visible over the Dynamics window, so the zoom lands below it
function drawFrame(ctx: CanvasRenderingContext2D, img: HTMLImageElement, cw: number, ch: number, set: FrameSet, i: number, dpr: number) {
  const cover = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
  const contain = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
  let s = set.fit === "contain" ? contain : cover;
  let fx = set.focusX, fy = set.focusY;
  let fill: string | null = set.fit === "contain" ? PAPER : null;
  let top = 0;
  if (set.fit !== "contain" && i >= set.containFrom) {
    const t = Math.min(1, (i - set.containFrom) / Math.max(1, set.count - 1 - set.containFrom));
    const e = t * t * (3 - 2 * t);
    top = HEADER * dpr * e;
    const containBelow = Math.min(cw / img.naturalWidth, (ch - HEADER * dpr) / img.naturalHeight);
    s = cover + (containBelow - cover) * e;
    fx += (0.5 - fx) * e; fy += (0.5 - fy) * e;
    fill = set.containFill;
  }
  const w = img.naturalWidth * s;
  const h = img.naturalHeight * s;
  if (fill) { ctx.fillStyle = fill; ctx.fillRect(0, 0, cw, ch); }
  ctx.drawImage(img, (cw - w) * fx, top + (ch - top - h) * fy, w, h);
}

const frameUrl = (set: FrameSet, i: number) => `${set.dir}/f${String(i).padStart(4, "0")}.webp`;

export function CinematicScroll({ pt, ctaHref, ctaLabel, signup, onVideo, compact }: Props) {
  const { t } = useLanguage();
  const [staticMode] = useState(prefersStatic);
  const [portrait, setPortrait] = useState(() => typeof window !== "undefined" && window.matchMedia(portraitQuery).matches);
  const pick = (p: boolean): FrameSet => { const l = CINEMATIC.sets[pt ? "pt" : "en"]; return p ? l.portrait : l.landscape; };
  const set: FrameSet = pick(portrait);

  const trackRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastWanted = useRef(0);
  const keepWanted = (i: number) => { lastWanted.current = i; };
  const requestFrame = useRef<(i: number) => void>(keepWanted);

  useEffect(() => {
    const mq = window.matchMedia(portraitQuery);
    const onChange = () => setPortrait(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Frame store + painter. Re-created when the orientation (frame set) changes.
  useEffect(() => {
    if (staticMode) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d", { alpha: false });
    if (!canvas || !ctx) return;

    let disposed = false;
    const imgs: Array<HTMLImageElement | undefined> = new Array(set.count);
    const ready = new Uint8Array(set.count);
    let drawn = -1;
    let want = lastWanted.current;
    let raf = 0;

    const nearestReady = (i: number) => {
      if (ready[i]) return i;
      for (let d = 1; d < set.count; d++) {
        if (i - d >= 0 && ready[i - d]) return i - d;
        if (i + d < set.count && ready[i + d]) return i + d;
      }
      return -1;
    };
    const paint = () => {
      raf = 0;
      const i = nearestReady(want);
      if (i < 0 || i === drawn) return;
      drawFrame(ctx, imgs[i]!, canvas.width, canvas.height, set, i, canvas.width / Math.max(1, canvas.clientWidth));
      drawn = i;
      canvas.dataset.frame = String(i);
      canvas.style.opacity = "1";
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(paint); };
    requestFrame.current = (i: number) => { lastWanted.current = i; want = i; schedule(); };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.round(r.width * dpr));
      canvas.height = Math.max(1, Math.round(r.height * dpr));
      drawn = -1;
      schedule();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    const order = loadOrder(set);
    let next = 0;
    let active = 0;
    const pump = () => {
      while (!disposed && active < 6 && next < order.length) {
        const i = order[next++];
        const img = new Image();
        img.decoding = "async";
        img.src = frameUrl(set, i);
        active++;
        img.decode()
          .then(() => { if (!disposed) { imgs[i] = img; ready[i] = 1; schedule(); } })
          .catch(() => {})
          .finally(() => { active--; pump(); });
      }
    };
    // Frames start lazily: on the first interaction, or 2.5 s after the page (and the poster, the LCP element) loaded.
    const wake = ["scroll", "wheel", "touchstart", "pointerdown", "keydown"] as const;
    let timer = 0;
    let started = false;
    const start = () => {
      if (started || disposed) return;
      started = true;
      wake.forEach((e) => window.removeEventListener(e, start));
      window.clearTimeout(timer);
      pump();
    };
    const arm = () => { timer = window.setTimeout(start, 2500); };
    wake.forEach((e) => window.addEventListener(e, start, { once: true, passive: true }));
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });

    return () => {
      disposed = true;
      ro.disconnect();
      window.removeEventListener("load", arm);
      wake.forEach((e) => window.removeEventListener(e, start));
      window.clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
      requestFrame.current = keepWanted;
    };
  }, [staticMode, set]);

  useGSAP(() => {
    if (staticMode || !trackRef.current) return;
    ScrollTrigger.config({ ignoreMobileResize: true });
    const q = gsap.utils.selector(trackRef);
    const state = { vh: 0 };
    const tl = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: { trigger: trackRef.current, start: "top top", end: "bottom bottom", scrub: 0.45 },
    });
    const railItems = q("[data-rail-item]") as HTMLElement[];
    let active = -2;
    tl.to(state, {
      vh: TRACK,
      duration: TRACK,
      onUpdate: () => {
        requestFrame.current(frameAt(state.vh, pick(portrait)));
        const phase = state.vh >= ZOOM ? "zoom" : "film";
        if (trackRef.current && trackRef.current.dataset.phase !== phase) trackRef.current.dataset.phase = phase;
        const now = RAIL.reduce((acc, at, i) => (state.vh >= at ? i : acc), -1);
        if (now !== active) {
          active = now;
          railItems.forEach((el, i) => el.setAttribute("data-active", String(i === now)));
        }
      },
    }, 0);
    tl.to(q("[data-rail]"), { scaleX: 1, duration: ZOOM }, 0);

    tl.to(q("[data-beat='0']"), { autoAlpha: 0, y: -12, duration: FADE + 2, ease: "power1.in" }, HERO_OUT);
    tl.to(q("[data-scrim]"), { opacity: 0, duration: FADE + 2 }, HERO_OUT);
    BEATS.forEach(([a, b], i) => {
      const el = q(`[data-beat='${i + 1}']`);
      tl.fromTo(el, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: FADE, ease: "power3.out" }, a);
      tl.to(q("[data-scrim]"), { opacity: 1, duration: FADE }, a - 2);
      tl.to(el, { autoAlpha: 0, y: -12, duration: FADE, ease: "power1.in" }, b);
      tl.to(q("[data-scrim]"), { opacity: 0, duration: FADE + 2 }, b);
    });

    // The rail is not part of the opening image: it fades in, tick by tick, once the visitor starts scrolling.
    tl.fromTo(q("[data-chrome]"), { autoAlpha: 0 }, { autoAlpha: 1, duration: 20 }, 8);
    tl.from(q("[data-rail-item]"), { x: -10, autoAlpha: 0, stagger: 3, duration: 12 }, 10);
    tl.to(q("[data-chrome]"), { autoAlpha: 0, duration: 8 }, ZOOM);
  }, { scope: trackRef, dependencies: [staticMode, portrait, pt], revertOnUpdate: true });

  // Rail click: scroll to where that chapter's caption is fully in.
  const jumpTo = (vh: number) => {
    const track = trackRef.current;
    if (!track) return;
    const top = track.getBoundingClientRect().top + window.scrollY;
    const unit = (track.offsetHeight - window.innerHeight) / TRACK;
    window.scrollTo({ top: top + (vh + FADE + 2) * unit, behavior: "smooth" });
  };

  // Below a sales hero the film opens as a section ("see how it works"), not as the page's headline.
  const heroCopy = compact ? (
    <>
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-[var(--fs-moss)]">{t("cinematic.section.eyebrow")}</p>
      <h2 className="mt-3 text-[2rem] font-semibold leading-[1.05] tracking-[-0.03em] text-[var(--fs-forest)] sm:text-5xl">{t("cinematic.section.title")}</h2>
      <p className="mt-4 max-w-md text-lg leading-relaxed text-gray-700">{t("cinematic.section.subtitle")}</p>
    </>
  ) : (
    <>
      <h1 className="text-[2rem] font-semibold leading-[1.05] tracking-[-0.03em] text-[var(--fs-forest)] sm:text-5xl lg:text-[3.75rem]">
        {t("cinematic.hero.title")}
      </h1>
      <p className="mt-4 max-w-md text-lg leading-relaxed text-gray-700">{t("cinematic.hero.subtitle")}</p>
      <div className="mt-7 flex flex-col gap-3 sm:flex-row">
        <a href={ctaHref} data-testid="cta-hero">
          <Button size="lg" className="h-12 w-full rounded-md bg-[var(--fs-moss)] px-6 text-base text-white hover:bg-[var(--fs-forest)] sm:w-auto">
            {ctaLabel}
          </Button>
        </a>
        <Button size="lg" variant="outline" onClick={onVideo} data-testid="cta-demo"
          className="h-12 w-full rounded-md border-gray-300 bg-white/80 px-6 text-base text-gray-800 backdrop-blur hover:bg-white sm:w-auto">
          <Play className="mr-2 h-4 w-4" />
          {t("cinematic.hero.demo")}
        </Button>
      </div>
      {signup && <p className="mt-3 text-sm text-gray-600">{t("cinematic.hero.free")}</p>}
    </>
  );

  const beatCopy = [
    <>
      <h2 className="text-[clamp(1.75rem,1.25rem+1.6vw,2.75rem)] font-semibold leading-[1.1] tracking-[-0.02em] text-[var(--fs-forest)]">{t("cinematic.b1.title")}</h2>
      <p className="mt-3 max-w-md text-lg leading-relaxed text-gray-700">{t("cinematic.b1.body")}</p>
    </>,
    <>
      <h2 className="text-[clamp(1.75rem,1.25rem+1.6vw,2.75rem)] font-semibold leading-[1.1] tracking-[-0.02em] text-[var(--fs-forest)]">{t("cinematic.b2.title")}</h2>
      <p className="mt-3 max-w-md text-lg leading-relaxed text-gray-700">{t("cinematic.b2.body")}</p>
    </>,
    <>
      <h2 className="text-[clamp(1.75rem,1.25rem+1.6vw,2.75rem)] font-semibold leading-[1.1] tracking-[-0.02em] text-[var(--fs-forest)]">{t("cinematic.b3.title")}</h2>
      <p className="mt-3 max-w-md text-lg leading-relaxed text-gray-700">{t("cinematic.b3.body")}</p>
      <dl className="mt-6 grid max-w-md grid-cols-3 gap-4 border-t border-black/10 pt-4 text-sm">
        <div><dt className="text-gray-500">{t("cinematic.legal.validity")}</dt><dd className="mt-1 font-medium">{t("cinematic.legal.validity.value")}</dd></div>
        <div><dt className="text-gray-500">{t("cinematic.legal.certificate")}</dt><dd className="mt-1 font-medium">{t("cinematic.legal.certificate.value")}</dd></div>
        <div><dt className="text-gray-500">{t("cinematic.legal.data")}</dt><dd className="mt-1 font-medium">{t("cinematic.legal.data.value")}</dd></div>
      </dl>
    </>,
  ];

  if (staticMode) {
    return (
      <section aria-label={t("cinematic.aria")} className="bg-white text-[#1B2A1E]" data-cinematic="static">
        <div className="fs-cine-panel relative flex items-end overflow-hidden landscape:items-center">
          <img src={set.poster} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: `${set.focusX * 100}% ${set.focusY * 100}%` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FAF7F0] via-[#FAF7F0]/60 to-transparent landscape:bg-gradient-to-r landscape:via-[#FAF7F0]/50" aria-hidden="true" />
          <div className="relative mx-auto w-full max-w-6xl px-5 pb-12 pt-24 sm:px-8"><div className="max-w-xl">{heroCopy}</div></div>
        </div>
        {beatCopy.map((copy, i) => (
          <div key={i} id={i === 0 ? "how" : undefined} className="mx-auto grid max-w-6xl scroll-mt-24 items-center gap-8 px-5 py-14 sm:px-8 lg:grid-cols-2 lg:py-20">
            <div>{copy}</div>
            <img src={set.panels[i + 1]} alt="" loading="lazy" className="w-full rounded-xl object-cover" />
          </div>
        ))}
      </section>
    );
  }

  return (
    <section ref={trackRef} aria-label={t("cinematic.aria")} className="fs-cine-track relative bg-[#F3F2F1] text-[#1B2A1E]" data-cinematic="film"
      style={{ ["--fs-cine-travel" as string]: TRACK } as CSSProperties}>
      {/* header anchor "Como funciona" lands just after the first beat has faded in */}
      <div id="how" aria-hidden="true" className="pointer-events-none absolute left-0 h-px w-px" style={{ top: `${BEATS[0][0] + FADE + 2}svh` }} />
      <div className="fs-cine-stage sticky top-0 w-full overflow-hidden" style={{ background: set.fit === "contain" ? PAPER : "#EFE9DF" }}>
        <img src={set.poster} alt="" aria-hidden="true" decoding="async" {...({ fetchpriority: "high" } as Record<string, string>)}
          className="absolute inset-0 h-full w-full"
          style={{ objectFit: set.fit, objectPosition: `${set.focusX * 100}% ${set.focusY * 100}%` }} />
        <canvas ref={canvasRef} aria-hidden="true" className="absolute inset-0 h-full w-full opacity-0" />
        <div data-scrim aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#FAF7F0]/95 via-[#FAF7F0]/55 via-40% to-transparent to-65% landscape:bg-gradient-to-r landscape:from-[#FAF7F0]/90 landscape:via-[#FAF7F0]/45 landscape:via-35% landscape:to-60%" />

        {/* caption layer: bottom third in portrait, left column in landscape; kept inside the small viewport */}
        <div className="fs-cine-captions absolute inset-x-0 top-16">
          <div className="relative mx-auto h-full w-full max-w-6xl">
            {[heroCopy, ...beatCopy].map((copy, i) => (
              <div key={i} data-beat={i}
                className="absolute inset-0 flex items-end px-5 pb-[max(2.5rem,env(safe-area-inset-bottom))] sm:px-8 landscape:items-center landscape:pb-0"
                style={i === 0 ? undefined : { visibility: "hidden", opacity: 0 }}>
                <div className={i === 0 ? "max-w-xl" : "max-w-md"}>{copy}</div>
              </div>
            ))}
          </div>
        </div>

        <div data-chrome className="absolute inset-x-0 bottom-0 lg:hidden">
          <div data-rail aria-hidden="true" className="h-[2px] w-full origin-left scale-x-0 bg-[var(--fs-sage)]" />
        </div>
        <nav data-chrome aria-label={t("cinematic.rail.aria")} className="absolute left-6 top-1/2 hidden -translate-y-1/2 lg:block">
          <span aria-hidden="true" className="absolute -bottom-10 -top-10 left-0 w-px bg-black/15" />
          <ul className="relative space-y-5">
            {RAIL.map((at, i) => (
              <li key={i}>
                <button type="button" data-rail-item={i} data-active="false" onClick={() => jumpTo(at)}
                  className="group flex items-center gap-3 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fs-moss)]">
                  <span aria-hidden="true" className="h-px w-3 bg-[#1B2A1E]/40 transition-all duration-300 group-hover:w-5 group-data-[active=true]:w-7 group-data-[active=true]:bg-[var(--fs-forest)]" />
                  <span className="hidden text-[11px] font-medium uppercase tracking-[0.18em] text-[#1B2A1E]/50 transition-colors group-hover:text-[#1B2A1E] group-data-[active=true]:text-[#1B2A1E] min-[1400px]:inline">
                    {t(`cinematic.rail.${i + 1}`)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  );
}
